import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { logout } from '../../../slices/authSlice';
import '../../DashboardHome/rush.css';
import '../../DashboardHome/dashboard.css';
import '../../DashboardHome/rush-fixes.css';
import '../../DashboardHome/onboarding.css';

const OI = {
    check: (
        <svg viewBox="0 0 16 16" width="15" height="15" fill="none">
            <path d="M3 8.5l3 3 7-7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    ),
    globe: (
        <svg viewBox="0 0 20 20" width="20" height="20" fill="none">
            <circle cx="10" cy="10" r="7" stroke="currentColor" strokeWidth="1.6" />
            <path d="M3 10h14M10 3c2 2 2 12 0 14M10 3c-2 2-2 12 0 14" stroke="currentColor" strokeWidth="1.6" />
        </svg>
    ),
    link: (
        <svg viewBox="0 0 20 20" width="20" height="20" fill="none">
            <path d="M8 12l4-4M9 5l1-1a3 3 0 014 4l-1 1M11 15l-1 1a3 3 0 01-4-4l1-1" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
    ),
    people: (
        <svg viewBox="0 0 20 20" width="20" height="20" fill="none">
            <circle cx="8" cy="8" r="3" stroke="currentColor" strokeWidth="1.6" />
            <path d="M2.5 16c.8-3 3-4.5 5.5-4.5S12.7 13 13.5 16" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            <circle cx="15" cy="7.5" r="2.2" stroke="currentColor" strokeWidth="1.6" />
        </svg>
    ),
    rocket: (
        <svg viewBox="0 0 20 20" width="20" height="20" fill="none">
            <path d="M10 2c3 1 5 4 5 8l-2 2H7l-2-2c0-4 2-7 5-8z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
            <circle cx="10" cy="8" r="1.5" stroke="currentColor" strokeWidth="1.6" />
            <path d="M7 14l-1.5 3M13 14l1.5 3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
    ),
    workspace: (
        <svg viewBox="0 0 20 20" width="20" height="20" fill="none">
            <rect x="3" y="4" width="14" height="12" rx="2" stroke="currentColor" strokeWidth="1.6" />
            <path d="M3 8h14M7 4v4" stroke="currentColor" strokeWidth="1.6" />
        </svg>
    ),
    arrow: (
        <svg viewBox="0 0 12 12" width="12" height="12" fill="none">
            <path d="M2 6h7M6 3l3 3-3 3" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    ),
    book: (
        <svg viewBox="0 0 16 16" width="15" height="15" fill="none">
            <path d="M3 3.5h4A1.5 1.5 0 018.5 5v8A1.2 1.2 0 007 12H3v-8.5z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
            <path d="M13 3.5H9A1.5 1.5 0 007.5 5v8A1.2 1.2 0 019 12h4V3.5z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
        </svg>
    ),
    play: (
        <svg viewBox="0 0 16 16" width="15" height="15" fill="none">
            <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.4" />
            <path d="M6.5 5.5l4 2.5-4 2.5z" fill="currentColor" />
        </svg>
    ),
    chat: (
        <svg viewBox="0 0 16 16" width="15" height="15" fill="none">
            <path d="M2.5 7a4 4 0 014-4h3a4 4 0 014 4v2a1 1 0 01-1 1H8l-3 2.5V10H3.5a1 1 0 01-1-1V7z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
        </svg>
    ),
};

const ProgressRing = ({ pct, size = 56 }) => {
    const r = (size - 7) / 2;
    const C = 2 * Math.PI * r;
    const len = (pct / 100) * C;
    return (
        <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size} className="ob-ring">
            <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--bg-soft)" strokeWidth="6" />
            <circle
                cx={size / 2} cy={size / 2} r={r}
                fill="none" stroke="var(--accent)" strokeWidth="6"
                strokeDasharray={`${len} ${C - len}`}
                strokeDashoffset={C * 0.25}
                strokeLinecap="round"
                transform={`rotate(-90 ${size / 2} ${size / 2})`}
                style={{ transition: 'stroke-dasharray 400ms ease' }}
            />
            <text x={size / 2} y={size / 2 + 1} textAnchor="middle" dominantBaseline="middle" className="ob-ring-t">
                {pct}%
            </text>
        </svg>
    );
};

const Onboarding = () => {
    const token = useSelector((s) => s.auth.token);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [projects, setProjects] = useState([]);
    const [team, setTeam] = useState([]);
    const [integrations, setIntegrations] = useState([]);
    const [voice, setVoice] = useState(null);
    const [doneOverrides, setDoneOverrides] = useState({});
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!token) return;
        const headers = { Authorization: `Bearer ${token}` };
        Promise.all([
            axios.get('/api/users/me', { headers }).then((r) => r.data),
            axios.get('/api/projects?summary=true', { headers }).then((r) => r.data).catch(() => ({})),
            axios.get('/api/team', { headers }).then((r) => r.data).catch(() => []),
            axios.get('/api/dashboard/integrations', { headers }).then((r) => r.data).catch(() => []),
            axios.get('/api/brand-voice', { headers }).then((r) => r.data).catch(() => null),
        ])
            .then(([me, projRes, teamRes, intRes, voiceRes]) => {
                setUser(me);
                const { createdProjects = [], sharedProjects = [] } = projRes || {};
                const unique = new Map([...createdProjects, ...sharedProjects].map((p) => [p._id, p]));
                setProjects([...unique.values()]);
                setTeam(Array.isArray(teamRes) ? teamRes : []);
                setIntegrations(Array.isArray(intRes) ? intRes : []);
                setVoice(voiceRes);
                setLoading(false);
            })
            .catch((err) => {
                if (err.response && err.response.status === 401) {
                    dispatch(logout());
                    navigate('/login');
                } else {
                    setLoading(false);
                }
            });
    }, [token, dispatch, navigate]);

    const STEP_DEFS = useMemo(() => [
        {
            id: 'workspace',
            icon: OI.workspace,
            title: 'Create your workspace',
            desc: `${user?.organization || user?.company || 'Your workspace'} is set up and ready to go.`,
            cta: 'View settings',
            href: '/dashboard/settings',
        },
        {
            id: 'locale',
            icon: OI.globe,
            title: 'Set your localization defaults',
            desc: 'Choose the source language and the targets new projects inherit.',
            cta: 'Configure',
            href: '/dashboard/settings',
        },
        {
            id: 'source',
            icon: OI.link,
            title: 'Connect a content source',
            desc: 'Sync files automatically from Drive, Figma, GitHub, and more.',
            cta: 'Connect a source',
            href: '/dashboard/sources',
        },
        {
            id: 'team',
            icon: OI.people,
            title: 'Invite your team',
            desc: 'Add translators, reviewers, and managers to collaborate.',
            cta: 'Invite people',
            href: '/dashboard/people',
        },
        {
            id: 'project',
            icon: OI.rocket,
            title: 'Create your first project',
            desc: 'Upload content, pick languages, and start translating.',
            cta: 'New project',
            href: '/dashboard/new-translation',
        },
    ], [user]);

    const done = useMemo(() => ({
        workspace: !!user,
        locale: !!(user?.workspaceSettings?.loc?.targets?.length),
        source: integrations.some((i) => i.connected),
        team: (team || []).length > 0,
        project: (projects || []).length > 0,
        ...doneOverrides,
    }), [user, integrations, team, projects, doneOverrides]);

    if (loading) return <div style={{ padding: 40, color: 'var(--ink-3)' }} className="rs-app">Loading…</div>;

    const completed = STEP_DEFS.filter((s) => done[s.id]).length;
    const total = STEP_DEFS.length;
    const pct = Math.round((completed / total) * 100);
    const allDone = completed === total;
    const nextIdx = STEP_DEFS.findIndex((s) => !done[s.id]);

    const toggle = (id) => setDoneOverrides((d) => ({ ...d, [id]: !done[id] }));

    const firstName = user?.firstName || (user?.email || '').split('@')[0] || 'there';

    return (
        <div style={{ minHeight: '100vh', background: 'var(--bg)', padding: 32 }} className="rs-app">
            <div className="ob-wrap" style={{ maxWidth: 1080, margin: '0 auto' }}>
                <div className="ob-main">
                    <div className={`ob-hero ${allDone ? 'done' : ''}`}>
                        <ProgressRing pct={pct} />
                        <div className="ob-hero-body">
                            <h1>{allDone ? `You're all set, ${firstName} 🎉` : `Welcome to Rush Studio, ${firstName}`}</h1>
                            <p>
                                {allDone
                                    ? 'Your workspace is fully configured. Jump in and start localizing.'
                                    : 'A few quick steps to get your localization workspace humming.'}
                            </p>
                        </div>
                        {allDone && (
                            <button type="button" className="ob-hero-cta" onClick={() => navigate('/dashboard')}>
                                Go to dashboard {OI.arrow}
                            </button>
                        )}
                    </div>

                    <div className="ob-progress-line">
                        <span><b>{completed}</b> of {total} complete</span>
                        <div className="bar"><span style={{ width: `${pct}%` }} /></div>
                    </div>

                    <div className="ob-steps">
                        {STEP_DEFS.map((s, i) => {
                            const isDone = !!done[s.id];
                            const isNext = i === nextIdx;
                            return (
                                <div className={`ob-step ${isDone ? 'done' : ''} ${isNext ? 'next' : ''}`} key={s.id}>
                                    <button
                                        type="button"
                                        className="ob-check"
                                        onClick={() => toggle(s.id)}
                                        aria-label={isDone ? 'Mark incomplete' : 'Mark complete'}
                                    >
                                        {isDone ? OI.check : <span className="ob-num">{i + 1}</span>}
                                    </button>
                                    <span className="ob-step-ic">{s.icon}</span>
                                    <div className="ob-step-body">
                                        <b>{s.title}</b>
                                        <span>{s.desc}</span>
                                    </div>
                                    {isDone ? (
                                        <span className="ob-step-done">Done</span>
                                    ) : (
                                        <button
                                            type="button"
                                            className={`ob-step-cta ${isNext ? 'primary' : ''}`}
                                            onClick={() => navigate(s.href)}
                                        >
                                            {s.cta} {OI.arrow}
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    <div className="ob-skip">
                        <button type="button" onClick={() => navigate('/dashboard')}>Skip setup for now</button>
                        <span>·</span>
                        <button type="button" onClick={() => navigate('/dashboard/new-translation')}>
                            Explore with a sample project
                        </button>
                    </div>
                </div>

                <aside className="ob-side">
                    <div className="ob-card">
                        <h3>Need a hand?</h3>
                        <p>Get up and running fast with a guide or a quick chat with our team.</p>
                        <button type="button" className="ob-res" onClick={() => navigate('/dashboard/help')}>
                            <span className="ic">{OI.book}</span>
                            <div>
                                <b>Read the setup guide</b>
                                <span>12 short articles</span>
                            </div>
                            {OI.arrow}
                        </button>
                        <button type="button" className="ob-res" onClick={() => navigate('/dashboard/help')}>
                            <span className="ic">{OI.play}</span>
                            <div>
                                <b>Watch the 3-min tour</b>
                                <span>See the whole flow</span>
                            </div>
                            {OI.arrow}
                        </button>
                        <button type="button" className="ob-res" onClick={() => navigate('/dashboard/help')}>
                            <span className="ic">{OI.chat}</span>
                            <div>
                                <b>Talk to an expert</b>
                                <span>Replies in ~2 min</span>
                            </div>
                            {OI.arrow}
                        </button>
                    </div>
                    <div className="ob-card tip">
                        <div className="ob-tip-k">Tip</div>
                        <p>
                            Press <span className="kbd">⌘</span><span className="kbd">K</span> anywhere to jump to any project, person, or page.
                        </p>
                    </div>
                </aside>
            </div>
        </div>
    );
};

export default Onboarding;
